/**
 * EventsPage -- Global event log with machine filter.
 */

import { useState } from 'react';
import { useEvents } from '../hooks/useEvents';
import { useMachines } from '../hooks/useMachines';
import { EventLog } from '../components/EventLog';

const PAGE_SIZE = 50;

export function EventsPage() {
  const [machineFilter, setMachineFilter] = useState<number | undefined>(undefined);

  const { machines } = useMachines();
  const { events, loading, error, refresh } = useEvents({
    machineId: machineFilter,
    limit: PAGE_SIZE,
    refreshInterval: 30_000,
  });

  function handleMachineChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value;
    setMachineFilter(val ? parseInt(val, 10) : undefined);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <h2>Events</h2>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap', flex: '1 1 auto', justifyContent: 'flex-end' }}>
          <select
            className="form-input"
            value={machineFilter ?? ''}
            onChange={handleMachineChange}
            style={{ minWidth: '160px', flex: '1 1 180px', maxWidth: 280 }}
          >
            <option value="">All Machines</option>
            {machines.map((m) => (
              <option key={m.machineId} value={m.machineId}>
                {m.name} (#{m.machineId})
              </option>
            ))}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card mb-6">
          <div className="card-body">
            <div style={{ color: 'var(--color-offline)', fontWeight: 500 }}>
              Error: {error}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          {loading && events.length === 0 ? (
            <div className="empty-state">
              <h3>Loading events...</h3>
            </div>
          ) : (
            <EventLog events={events} />
          )}
        </div>
      </div>

      {/* Pagination info */}
      {events.length > 0 && (
        <div className="flex items-center justify-between mt-4" style={{ fontSize: '0.85rem' }}>
          <span className="text-muted">
            Showing {events.length} events
            {machineFilter ? ` for machine #${machineFilter}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
